type Command = [command: string, ...args: unknown[]];
type Dispatch = (...args: Command) => void;

/** Own the queue until a runtime is ready; Partytown discards its forwarding
 * queue on fallback. A withdrawal must also discard unsent identity/events. */
export class MetaRuntimeQueue {
	private pending: Command[] = [];
	private dispatch: Dispatch | null = null;
	private allowed = false;

	consent(allowed: boolean) {
		this.allowed = allowed;
		if (!allowed) this.pending = [];
		this.dispatch?.('consent', allowed ? 'grant' : 'revoke');
	}

	send(...args: Command) {
		if (!this.allowed) return;
		if (this.dispatch) this.dispatch(...args);
		else this.pending.push(args);
	}

	ready(dispatch: Dispatch) {
		// A late worker must never receive a second copy after fallback wins.
		if (this.dispatch) return false;
		this.dispatch = dispatch;
		dispatch('consent', this.allowed ? 'grant' : 'revoke');
		const pending = this.pending.splice(0);
		if (this.allowed) for (const args of pending) dispatch(...args);
		return true;
	}
}

const queue = new MetaRuntimeQueue();
let started = false;
const SDK = 'https://connect.facebook.net/en_US/fbevents.js';
const READY = 'workman-meta-runtime-ready';

declare global {
	interface Window {
		workmanMetaDispatch?: Dispatch;
	}
}

// Both the worker and Partytown's built-in main-thread fallback execute this.
// No pixel is initialized and no event is sent until our queue selects it.
const bootstrap = `(function(){
  var f = function(){f.callMethod ? f.callMethod.apply(f,arguments) : f.queue.push(arguments)};
  f.queue=[]; f.push=f; f.loaded=true; f.version='2.0'; f.disablePushState=true;
  window.fbq=f; window._fbq=f;
  window.workmanMetaDispatch=function(){f.apply(null,arguments)};
  var s=document.createElement('script'); s.async=true; s.src=${JSON.stringify(SDK)};
  s.onload=function(){window.dispatchEvent(new CustomEvent(${JSON.stringify(READY)}))};
  document.head.appendChild(s);
})();`;

export function setMetaWorkerConsent(allowed: boolean) {
	queue.consent(allowed);
}

export function sendMetaWorkerCommand(...args: Command) {
	queue.send(...args);
}

export function startMetaWorker() {
	if (started) return;
	started = true;
	let settled = false;
	let directStarted = false;
	let timer: ReturnType<typeof setTimeout>;
	const settle = (dispatch: Dispatch) => {
		if (settled) return;
		settled = true;
		clearTimeout(timer);
		window.removeEventListener(READY, onReady);
		queue.ready(dispatch);
	};
	const onReady = () => {
		if (window.workmanMetaDispatch) settle(window.workmanMetaDispatch);
	};
	const direct = () => {
		if (settled || directStarted) return;
		directStarted = true;
		// The built-in fallback may already have started the direct SDK. Reuse
		// that instance so two script loads cannot initialize different queues.
		if (window.fbq) {
			if (window.fbq.callMethod) settle(window.fbq);
			return;
		}
		// A separate saved dispatcher makes a late worker initialization harmless.
		const f = ((...args: Command) => {
			if (f.callMethod) f.callMethod(...args);
			else f.queue.push(args);
		}) as Dispatch & {
			callMethod?: Dispatch;
			queue: Command[];
			push?: Dispatch;
			loaded: boolean;
			version: string;
			disablePushState: boolean;
		};
		f.queue = [];
		f.push = f;
		f.loaded = true;
		f.version = '2.0';
		f.disablePushState = true;
		window.fbq = f;
		window._fbq = f;
		const script = document.createElement('script');
		script.async = true;
		script.src = SDK;
		script.onload = () => settle(f);
		document.head.appendChild(script);
	};

	window.addEventListener(READY, onReady);
	// This is failure recovery, never a visitor-interaction gate. It also
	// covers a failed proxy/SDK after the worker itself initialized successfully.
	timer = setTimeout(direct, 4000);
	const script = document.createElement('script');
	script.type = 'text/partytown';
	script.textContent = bootstrap;
	document.head.appendChild(script);
	const loader = document.createElement('script');
	loader.src = '/_meta-worker/init.js';
	loader.async = true;
	loader.onerror = direct;
	document.head.appendChild(loader);
}
